import * as router from 'plugins/router';
import * as analytics from 'analytics';

import { ApiService } from '@/features/api';

import { API_PREFIX } from '../api/constants';
import QuickBooksIcon from '../assets/quickbooks-icon.svg';
import AccountingSoftwareCompany from '../types/AccountingSoftwareCompany';
import AccountingSoftwareContact from '../types/AccountingSoftwareContact';
import Bill from '../types/Bill';
import BillData from '../types/BillData';
import IAccount from '../types/IAccount';
import Invoice from '../types/Invoice';
import InvoiceData from '../types/InvoiceData';
// eslint-disable-next-line @cspell/spellchecker
import IQBWCFile from '../types/IQBWCFile';
import QBWCFileLink from '../types/QBWCFileLink';
import { SupportedAccountingSoftware } from '../types/SupportedAccountingSoftware';

import BaseAccountingSoftwarePackage from './BaseAccountingSoftwarePackage';

class QuickBooksDesktopAccountingPackage extends BaseAccountingSoftwarePackage {
  // TODO: remove vendor logo from public dir once we fully migrate to the new UI
  readonly logoUrl = `${import.meta.env.APP_STATIC_APP_ROOT_URL}/img/vendor/quickbooks-icon.svg`;

  readonly icon = QuickBooksIcon;

  supportsInvoices = true;

  constructor() {
    super(SupportedAccountingSoftware.QuickBooksDesktop);
  }

  ConnectConnectIcon(): string {
    return 'third_party_quickbooks_desktop_connect';
  }

  FilterResolutions(conflictingAccountName: string): string {
    // The name for Plooto Instant in US is too long. We have a shorter name
    if (conflictingAccountName == 'Plooto Instant (United States - USD)') {
      return 'Plooto Instant (US - USD)';
    }
    let newAccountName = conflictingAccountName;
    newAccountName = newAccountName
      .replace(':', '')
      .replace(';', '')
      // eslint-disable-next-line no-control-regex
      .replace(/[^\x00-\xFF]/g, '')
      .trim();
    if (newAccountName.length > 31) {
      newAccountName = newAccountName.substring(0, 31);
    }
    return newAccountName;
  }

  // callback that is called via pop-up when OAuth has been completed
  async Connected(info: any): Promise<void> {
    const companyId: string = this.companyService.CompanyId();

    await ApiService.put(
      `${
        import.meta.env.APP_URLS_API_URL
      }/${API_PREFIX}/companies/${companyId}/quickbooksonline/oauth`,
      { body: info }
    );

    this.fireAccountingSoftwareConnectedEvent();
    analytics.send('event', 'Company-AccountingSoftware', 'Connect', 'QBD');
  }

  // called via callback once user clicks on "connect" button
  async Connect(): Promise<any> {
    router.navigate('user/integrations/quickbooksDesktop');
  }

  // generates a clickable link that starts connection process
  GenerateConnectButton($target: JQuery, context?: string): void {
    $('<a/>', {
      href: '#',
      class: 'third_party_quickbooks_desktop_connect',
    })
      .appendTo($target)
      .click(() => {
        this.fireAccountingSoftwareConnectingEvent(context);
        this.Connect();
        return false;
      });
  }

  async DownloadQBWC(): Promise<QBWCFileLink> {
    const companyId: string = this.companyService.CompanyId();

    const qbwcFile = await ApiService.get<IQBWCFile>(
      `${
        import.meta.env.APP_URLS_API_URL
      }/${API_PREFIX}/companies/${companyId}/quickbooksdesktop/generateQBWC`
    );

    const downloadUrl = `${
      import.meta.env.APP_URLS_API_URL
    }/${API_PREFIX}/companies/${companyId}/quickbooksdesktop/downloadQBWC/${qbwcFile.templateId}`;

    return new QBWCFileLink(qbwcFile, downloadUrl);
  }

  async AddAccount(account: any): Promise<IAccount> {
    const companyId: string = this.companyService.CompanyId();

    const response = await ApiService.put<IAccount>(
      `${
        import.meta.env.APP_URLS_API_URL
      }/${API_PREFIX}/companies/${companyId}/quickbooksdesktop/accounts`,
      { body: account }
    );

    return response;
  }

  async GetContacts(): Promise<Array<AccountingSoftwareContact>> {
    const companyId: string = this.companyService.CompanyId();

    const contacts = await ApiService.get<Array<AccountingSoftwareContact>>(
      `${
        import.meta.env.APP_URLS_API_URL
      }/${API_PREFIX}/companies/${companyId}/quickbooksdesktop/contacts`
    );

    return contacts;
  }

  async GetAccounts(): Promise<Array<IAccount>> {
    const companyId: string = this.companyService.CompanyId();

    const accounts = await ApiService.get<Array<IAccount>>(
      `${
        import.meta.env.APP_URLS_API_URL
      }/${API_PREFIX}/companies/${companyId}/quickbooksdesktop/accounts`
    );

    return accounts;
  }

  async fetchCompany(): Promise<AccountingSoftwareCompany> {
    const companyId: string = this.companyService.CompanyId();

    const company = await ApiService.get<AccountingSoftwareCompany>(
      `${
        import.meta.env.APP_URLS_API_URL
      }/${API_PREFIX}/companies/${companyId}/quickbooksdesktop/company`
    );

    return company;
  }

  async GetBills(): Promise<Array<Bill>> {
    const companyId: string = this.companyService.CompanyId();

    const billData = await ApiService.get<Array<BillData>>(
      `${
        import.meta.env.APP_URLS_API_URL
      }/${API_PREFIX}/companies/${companyId}/quickbooksdesktop/bills`
    );

    if (companyId !== this.companyService.CompanyId()) {
      throw new Error('Company switched while fetching bills');
    }

    return billData.map((data) => new Bill(data));
  }

  async GetInvoices(): Promise<Array<Invoice>> {
    const companyId: string = this.companyService.CompanyId();

    const invoiceData = await ApiService.get<Array<InvoiceData>>(
      `${
        import.meta.env.APP_URLS_API_URL
      }/${API_PREFIX}/companies/${companyId}/quickbooksdesktop/invoices`
    );

    if (companyId !== this.companyService.CompanyId()) {
      throw new Error('Company switched while fetching invoices');
    }

    return invoiceData.map((data) => new Invoice(data));
  }

  async GetReconciliationStatus(): Promise<string> {
    const companyId: string = this.companyService.CompanyId();

    const reconciliationStatus = await ApiService.get<string>(
      `${
        import.meta.env.APP_URLS_API_URL
      }/${API_PREFIX}/companies/${companyId}/quickbooksdesktop/reconciliationStatus`
    );

    return reconciliationStatus;
  }
}

export default QuickBooksDesktopAccountingPackage;
